/**
 * Global Toast Override for Mobile Platforms
 *
 * This module patches the Sonner toast to use haptic feedback on mobile
 * instead of showing visual toasts for success/info/warning.
 *
 * Import this file once in App.tsx to apply globally:
 * ```ts
 * import '@/lib/toast-override';
 * ```
 *
 * Behavior:
 * - Mobile (iOS/Android): success/info/warning → haptic only
 * - Mobile: error → haptic + visual toast
 * - Web: all toasts show normally
 */
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';

// Only apply override on native platforms
if (Capacitor.isNativePlatform()) {
  // Store original methods
  const originalSuccess = toast.success.bind(toast);
  const originalInfo = toast.info.bind(toast);
  const originalWarning = toast.warning.bind(toast);
  const originalError = toast.error.bind(toast);
  const originalMessage = toast.message?.bind(toast);

  // Helper to trigger haptic
  const haptic = async (type: 'success' | 'error' | 'warning' | 'info') => {
    try {
      switch (type) {
        case 'success':
          await Haptics.notification({ type: NotificationType.Success });
          break;
        case 'error':
          await Haptics.notification({ type: NotificationType.Error });
          break;
        case 'warning':
          await Haptics.notification({ type: NotificationType.Warning });
          break;
        case 'info':
          await Haptics.impact({ style: ImpactStyle.Light });
          break;
      }
    } catch {
      // Haptics not available
    }
  };

  // Override success - haptic only on mobile
  toast.success = ((message: any, options?: any) => {
    haptic('success');
    // Don't show visual toast on mobile for success
    return undefined as any;
  }) as typeof toast.success;

  // Override info - haptic only on mobile
  toast.info = ((message: any, options?: any) => {
    haptic('info');
    return undefined as any;
  }) as typeof toast.info;

  // Override warning - haptic only on mobile
  toast.warning = ((message: any, options?: any) => {
    haptic('warning');
    return undefined as any;
  }) as typeof toast.warning;

  // Override error - haptic + visual (errors are important)
  toast.error = ((message: any, options?: any) => {
    haptic('error');
    return originalError(message, options);
  }) as typeof toast.error;

  // Override message - haptic only on mobile
  if (originalMessage) {
    toast.message = ((message: any, options?: any) => {
      haptic('info');
      return undefined as any;
    }) as typeof toast.message;
  }

  console.debug('[Toast Override] Mobile haptic mode enabled');
}

export {};
